import React, { useEffect } from 'react';
import {
    Home,
    FileText,
    CheckSquare,
    Settings,
    Code,
    Sparkles,
    ArrowRight,
    X,
    LogOut,
    LogIn
} from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Outlet, useNavigate, useParams, useLocation, NavLink } from 'react-router-dom';
import ColorThief from 'colorthief';
import { ProjectProvider, useProjectContext } from '../../context/ProjectContext';
import { DraftDockProvider } from '../../context/DraftDockContext';
import DraftDockHost from '../../components/drafts/DraftDockHost';
import ProjectMobileNav from '../../components/ProjectMobileNav';
import AmbientCanvas from '../../components/layout/AmbientCanvas';
import { Surface } from '../../components/layout/Surface';
import Tip from '../../components/ui/tip';

// Every CSS var the project-icon palette overwrites. Kept in one place so the
// reset path can't drift out of sync with the write path.
const THEMED_VARS = ['--primary', '--ring', '--primary-foreground', '--accent', '--accent-foreground'];

const MainNavLink = ({ to, label, icon: Icon }) => (
    <NavLink
        to={to}
        className={({ isActive }) => `
            flex flex-col items-center justify-center gap-1.5 w-16 h-16 rounded-xl
            transition-all duration-200 group
            ${isActive ? 'bg-primary text-primary-foreground shadow-lg scale-105' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}
        `}
    >
        <Icon size={20} strokeWidth={2} />
        <span className="text-[9px] font-medium uppercase tracking-wider">{label}</span>
    </NavLink>
);

export default function ProjectLayout({ projectId: propProjectId, token: propToken, readOnly: propReadOnly }) {
    const params = useParams();
    const projectId = propProjectId || params.projectId;

    return (
        <ProjectProvider projectId={projectId} token={propToken}>
            <ProjectLayoutContent />
        </ProjectProvider>
    );
}

function ProjectLayoutContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { project, readOnly, projectId, token } = useProjectContext();
    const baseUrl = token ? `/s/${token}` : `/project/${projectId}`;
    const isAdvisorRoute = location.pathname.endsWith('/chat');

    // Dynamic Theme Logic - enhances palette with icon colors when available.
    //
    // These vars drive far more than a border now: AmbientCanvas paints the whole
    // app background from --primary/--accent, so a stale value is loudly visible.
    // Hence the reset-on-no-icon, the cancelled guard against a slow image
    // resolving after we've navigated on, and the cleanup on unmount.
    useEffect(() => {
        const root = document.documentElement;
        const reset = () => THEMED_VARS.forEach(v => root.style.removeProperty(v));

        if (!project?.icon) {
            reset();
            return undefined;
        }

        let cancelled = false;
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = project.icon;

        img.onerror = () => { if (!cancelled) reset(); };

        img.onload = () => {
            if (cancelled) return;
            try {
                const colorThief = new ColorThief();

                // Extract dominant color and a palette for richer theming
                const dominantColor = colorThief.getColor(img);
                const palette = colorThief.getPalette(img, 3); // Get top 3 colors

                const [r, g, b] = dominantColor;
                const { h, s, l } = rgbToHsl(r, g, b);

                // Get secondary color from palette (if different enough from dominant)
                let [r2, g2, b2] = palette[1] || dominantColor;
                const { h: h2, s: s2, l: l2 } = rgbToHsl(r2, g2, b2);

                // Primary color - for buttons, active states, main accents
                root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
                root.style.setProperty('--ring', `${h} ${s}% ${l}%`);

                // Adjust primary-foreground based on luminance for readability
                root.style.setProperty('--primary-foreground', l > 50 ? '0 0% 10%' : '0 0% 98%');

                // Accent color from palette - for highlights, secondary elements
                root.style.setProperty('--accent', `${h2} ${s2}% ${l2}%`);
                root.style.setProperty('--accent-foreground', l2 > 50 ? '0 0% 10%' : '0 0% 98%');
            } catch (e) {
                console.error("Color extraction failed", e);
                reset();
            }
        };

        return () => { cancelled = true; reset(); };
    }, [project?.icon]);

    // Global Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (readOnly) return; // Disable shortcuts in read-only mode

            const activeEl = document.activeElement;

            // ⌘⇧J puts a second draft beside the first on the project console.
            // Checked before ⌘J, and ⌘J now checks `!e.shiftKey`: `e.key` for
            // ⌘⇧J is "J", which lowercases to the same letter, so without the
            // guard this chord would just open a blank draft.
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('toggle-draft-split'));
                return;
            }

            // ⌘J opens a blank scratchpad from anywhere — including from inside
            // the chat composer, which is exactly when you want to park a thought
            // without losing the sentence you were writing. It therefore has to
            // be handled before both the input guard and the modifier bail below.
            if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('open-draft'));
                return;
            }

            // A React handler on the root has already claimed this key. Window
            // listeners are last in the bubble, so this is a reliable test —
            // for handlers on the tree, at least; see the dock guard below.
            if (e.defaultPrevented) return;

            // The scratchpad is a document with its own keyboard model, and a
            // block selection in it is not "nothing is focused": the editor root
            // holds focus, so none of the guards below would fire and `d` would
            // both type a letter and open the new-doc dialog. Tabbing into a
            // draft has the same problem, where `1`-`5` navigated away mid-edit.
            //
            // Tested by attribute rather than by `defaultPrevented`, because the
            // dock is lazy-loaded and this listener is registered first — its
            // own window handler runs after this one and would be too late.
            if (activeEl.closest?.('[data-draft-dock]')) return;

            // Ignore if input/textarea is focused, or within Monaco Editor, or contenteditable
            // Monaco Editor uses synthetic inputs, so we check for its container class
            if (['INPUT', 'TEXTAREA'].includes(activeEl.tagName)) return;
            if (activeEl.isContentEditable) return;
            if (activeEl.closest('.monaco-editor')) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return; // Avoid conflict with browser shortcuts

            switch (e.key.toLowerCase()) {
                // Quick Actions
                case 'q':
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('open-draft'));
                    break;
                case 'n':
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('open-new-task'));
                    break;
                case 'd':
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('open-new-doc'));
                    break;
                case 'u':
                    e.preventDefault();
                    // Dispatch only. This used to also click #quick-up directly,
                    // and the route listening for the event clicks the same input
                    // — so `u` opened the file picker twice.
                    window.dispatchEvent(new CustomEvent('trigger-upload'));
                    break;

                // Navigation
                case '1': e.preventDefault(); navigate(`${baseUrl}/home`); break;
                case '2': e.preventDefault(); navigate(`${baseUrl}/chat`); break;
                case '3': e.preventDefault(); navigate(`${baseUrl}/files`); break;
                case '4': e.preventDefault(); navigate(`${baseUrl}/editor`); break;
                case '5': e.preventDefault(); navigate(`${baseUrl}/tasks`); break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // `readOnly` and `baseUrl` are read inside the handler and were missing
        // here: both were captured at mount, so on a share link every navigation
        // shortcut went to the wrong URL and the read-only guard used whatever
        // value happened to exist before the project resolved.
    }, [readOnly, baseUrl, navigate]);

    const mainNavItems = [
        { to: `${baseUrl}/home`, label: 'Home', icon: Home },
        { to: `${baseUrl}/chat`, label: 'Advisor', icon: Sparkles },
        { to: `${baseUrl}/tasks`, label: 'Tasks', icon: CheckSquare },
        { to: `${baseUrl}/files`, label: 'Artifacts', icon: FileText },
        { to: `${baseUrl}/editor`, label: 'Editor', icon: Code },
    ].filter(Boolean);

    return (
        <DraftDockProvider projectId={projectId} readOnly={readOnly}>
        <div className="h-screen w-screen bg-background flex antialiased overflow-hidden relative">

            {/* The atmosphere every surface floats on. Must be first: z-0. */}
            <AmbientCanvas />

            {/* Navigation Sidebar - Desktop Only.
                No background of its own: it sits directly on the canvas, which is
                the point. Its own buttons carry enough chrome to stay legible. */}
            <nav className="hidden md:flex w-20 flex-shrink-0 flex-col items-center pt-8 pb-3 z-20 h-full">
                {/* 1. App Icon - uses static favicon */}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-xl mb-6 cursor-pointer overflow-hidden transition-transform hover:scale-105" onClick={() => navigate('/dashboard')}>
                    <img src="/favicon-96x96.png" alt="Forge AI" className="w-full h-full object-contain" />
                </div>

                {/* Main Navigation - centered with flex-1 */}
                <div className="flex-1 flex flex-col items-center justify-center gap-4 w-full px-2">
                    {mainNavItems.map(item => (
                        <MainNavLink key={item.to} {...item} />
                    ))}
                </div>

                {/* Utilities - at absolute bottom */}
                <div className="flex flex-col items-center gap-3">
                    {/* `right` throughout: this is a 5rem rail pinned to the
                        left edge, so anything else opens over the content. */}
                    {!readOnly && (
                        <Tip label="Settings" side="right">
                            <NavLink
                                to={`${baseUrl}/settings`}
                                data-testid="nav-settings"
                                className={({ isActive }) => `
                                    p-3 rounded-xl transition-all duration-200
                                    ${isActive
                                        ? 'bg-primary/20 text-primary shadow-sm'
                                        : 'bg-secondary/30 border border-border/50 text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/30'}
                                `}
                            >
                                <Settings size={20} />
                            </NavLink>
                        </Tip>
                    )}

                    {readOnly ? (
                        <Tip label="Log in" side="right">
                            <button
                                onClick={() => window.location.href = '/login'}
                                data-testid="nav-login"
                                className="flex items-center gap-2 p-3 text-muted-foreground hover:text-primary transition-colors text-xs"
                            >
                                <LogIn size={18} />
                            </button>
                        </Tip>
                    ) : (
                        <Tip label="Exit project" side="right">
                            <button
                                onClick={() => navigate('/dashboard')}
                                data-testid="nav-exit-project"
                                className="p-3 text-muted-foreground hover:text-destructive transition-colors"
                            >
                                <LogOut size={20} />
                            </button>
                        </Tip>
                    )}
                </div>
            </nav>

            {/* Mobile Navigation */}
            <ProjectMobileNav baseUrl={baseUrl} readOnly={readOnly} />

            {/* Surface column. This wrapper owns the outer inset and the gap
                between surfaces; it paints nothing itself. `md:pl-0` keeps the
                first surface flush against the nav rail. All spacing is `md:`
                so mobile stays edge-to-edge. */}
            <div className="relative z-10 flex h-screen min-w-0 flex-1 flex-col p-0 md:gap-3 md:py-3 md:pr-3 md:pl-0">
                {/* Not on the Advisor route: the showcase there opens with its
                    own pitch and closes with its own CTA, and stacking a third
                    one above them left the demo boxed in by its own marketing. */}
                {readOnly && !isAdvisorRoute ? <ReadOnlyCta /> : null}

                {/* The surface row. Routes render their own <Surface> elements
                    straight into here — no wrapper, or we'd just rebuild the
                    single-card problem one level down. No overflow-hidden
                    either: it would clip the surfaces' shadows into the gap. */}
                <main className="flex min-h-0 min-w-0 flex-1 flex-row md:gap-3">
                    <Outlet />
                </main>
            </div>

            {/* The scratchpad. A peer of the surface column rather than a child
                of any route, so it survives navigation — and `fixed` at z-40:
                over the surfaces (z-10), under the mobile nav and Radix
                overlays (z-50). */}
            <DraftDockHost />
        </div>
        </DraftDockProvider>
    );
}

// Dismissal outlives the tab: a visitor who has waved the CTA away once should
// not meet it again on every shared link they open.
const CTA_DISMISSED_KEY = 'forge:shared-cta-dismissed';

const readCtaDismissed = () => {
    try {
        return window.localStorage.getItem(CTA_DISMISSED_KEY) === '1';
    } catch {
        // Safari private mode / storage disabled. Showing the strip is the safe
        // failure here — it is dismissable in-session either way.
        return false;
    }
};

/**
 * CTA shown to read-only (shared-link) visitors, above the surface row.
 *
 * Deliberately a *status strip*, not a hero. The visitor came to look at
 * someone's project, and the previous card — 10x10 icon tile, two-line pitch,
 * full-width amber button — took a third of the viewport on mobile and outweighed
 * the project it was advertising against. This is one line of terminal-weight
 * micro-type at every breakpoint: read-only marker, pitch, compact action, ✕.
 * The copy is the only thing that changes between mobile and desktop.
 */
function ReadOnlyCta() {
    const [dismissed, setDismissed] = React.useState(readCtaDismissed);
    const reduceMotion = useReducedMotion();

    const dismiss = () => {
        setDismissed(true);
        try {
            window.localStorage.setItem(CTA_DISMISSED_KEY, '1');
        } catch {
            /* session-only dismissal is still a dismissal */
        }
    };

    return (
        <AnimatePresence initial={false}>
            {!dismissed && (
                // Height is animated (not just opacity) so the surfaces below
                // slide up with the strip instead of snapping when it unmounts.
                <motion.div
                    key="readonly-cta"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="flex-shrink-0 overflow-hidden"
                >
                    <Surface
                        as="aside"
                        variant="banner"
                        data-testid="readonly-cta"
                        className="group/cta z-20 flex-row items-center gap-2.5 px-3 py-2 md:gap-4 md:px-4 md:py-2.5"
                    >
                        {/* Tracing hairline in the project's own colour: the one
                            piece of ornament, and the only thing tying the strip
                            to the palette now that the gradient is gone. */}
                        <span
                            aria-hidden
                            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"
                        />

                        {/* Status marker. Desktop gets the full chip; mobile keeps
                            just the pulse, which is all the room there is. */}
                        <span className="flex flex-shrink-0 items-center gap-2 rounded-md border-primary/25 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-primary md:border md:bg-primary/[0.08] md:px-2 md:py-1">
                            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))] motion-safe:animate-pulse" />
                            <span className="hidden md:inline">read-only</span>
                        </span>

                        {/* The visitor has just spent a minute looking at a real
                            project, so the pitch leans on that instead of
                            re-explaining the product: question first, mechanism
                            second. No claim about how *this* project was made —
                            we don't know, and guessing wrong in front of its
                            owner is worse than saying less. */}
                        <p className="min-w-0 flex-1 truncate text-[12px] leading-none text-white/45 md:text-[13px]">
                            <span className="font-semibold text-white/90">Want a project like this?</span>
                            <span className="hidden md:inline"> Describe yours in a chat, get mockups, tasks and docs.</span>
                        </p>

                        <a
                            href="/register"
                            data-testid="readonly-cta-signup"
                            className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/15 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-primary transition-colors duration-150 hover:border-primary hover:bg-primary hover:text-primary-foreground md:px-3 md:text-[11px]"
                        >
                            <span className="md:hidden">Start free</span>
                            <span className="hidden md:inline">Build yours free</span>
                            <ArrowRight size={12} strokeWidth={2.5} />
                        </a>

                        <button
                            type="button"
                            onClick={dismiss}
                            aria-label="Dismiss Forge AI banner"
                            data-testid="readonly-cta-dismiss"
                            className="flex-shrink-0 rounded-md p-1.5 text-white/35 transition-colors duration-150 hover:bg-white/5 hover:text-white/80"
                        >
                            <X size={14} strokeWidth={2.5} />
                        </button>
                    </Surface>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
